import { createConnector } from "wagmi";
import { injected } from "wagmi/connectors";

import {
  chosenOnly,
  keepPermissions,
  seedChosen,
} from "@/lib/wallet/evm-chosen";
import { isGenericEnabled } from "@/lib/wallet/solana/allowlist";

import type { Wallet, WalletDetailsParams } from "@rainbow-me/rainbowkit";
import type { EIP1193Provider } from "viem";

/**
 * MetaMask's EVM side, once MetaMask is also offered as a Solana wallet
 * (`isOffered("metamask")`). Inert otherwise: `metaMaskEntry` never reaches
 * this module, and the picker is what it was. Ported from the MetaMask
 * branch.
 *
 * MetaMask keeps one permission per site for every chain it serves.
 * Connecting it for Solana grants this origin its Ethereum accounts as well,
 * which is exactly the Phantom hazard `phantom-evm.ts` exists for, so its
 * entry gets the same `chosenOnly` rule. The permission being shared adds a
 * second rule: wagmi's disconnect sends `wallet_revokePermissions`, which
 * MetaMask applies to the *whole* permission, Solana included, so it is
 * answered here (`keepPermissions`).
 */
export const METAMASK_EVM_CHOSEN = "mudwater.metamask-evm.chosen";

/** A browser with several wallets installed lists them all here, and only one
 * of them also holds `window.ethereum` itself. */
export function injectedProviders(injected: unknown): unknown[] {
  if (typeof injected !== "object" || injected === null) return [];
  const announced: unknown = Reflect.get(injected, "providers");
  return Array.isArray(announced) ? announced : [injected];
}

/**
 * Wallets that set `isMetaMask` to pass for it, as wagmi's own `metaMask`
 * target lists them (`@wagmi/core` `connectors/injected.js`).
 */
const IMPERSONATORS = [
  "isApexWallet",
  "isAvalanche",
  "isBitKeep",
  "isBlockWallet",
  "isBraveWallet",
  "isKuCoinWallet",
  "isMathWallet",
  "isOkxWallet",
  "isOKExWallet",
  "isOneInchIOSWallet",
  "isOneInchAndroidWallet",
  "isOpera",
  "isPhantom",
  "isPortal",
  "isRabby",
  "isTokenPocket",
  "isTokenary",
  "isUniswapWallet",
  "isZerion",
];

/** MetaMask itself: never a wallet that only claims its flag. */
export function isGenuineMetaMask(provider: unknown): boolean {
  if (typeof provider !== "object" || provider === null) return false;
  if (!Reflect.get(provider, "isMetaMask")) return false;
  return IMPERSONATORS.every((flag) => !Reflect.get(provider, flag));
}

function isEip1193(value: unknown): value is EIP1193Provider {
  return (
    typeof value === "object" &&
    value !== null &&
    typeof Reflect.get(value, "request") === "function" &&
    typeof Reflect.get(value, "on") === "function" &&
    typeof Reflect.get(value, "removeListener") === "function"
  );
}

/**
 * In order of preference: Flask only where the stable build is absent.
 *
 * On every network, unlike the Solana side, where Flask counts only on
 * devnet (review A, N5). Deliberately: an EVM provider has no network of its
 * own to be wrong about, Flask's is as genuinely MetaMask's as the stable
 * build's, and this entry exists only where MetaMask is offered for Solana,
 * which production never does.
 */
const METAMASK_RDNS = ["io.metamask", "io.metamask.flask"];

/**
 * MetaMask as it announces itself over EIP-6963, which holds wherever it is
 * installed — including when Phantom's "default wallet" setting owns
 * `window.ethereum` with no `providers` list, where the flag lookup alone
 * would find nothing.
 *
 * Only a genuine MetaMask is taken from an announcement: Rabby, in
 * MetaMask-replacement mode, announces `io.metamask` too, and would otherwise
 * become the gated "MetaMask" (`optionc-plan.md` §3.1.6).
 *
 * Asked and answered synchronously: the request is an event and the wallet
 * announces from its handler. A wallet that answered later would simply fall
 * through to the flag lookup.
 */
function announcedMetaMask(): EIP1193Provider | undefined {
  if (typeof window === "undefined") return undefined;
  const found = new Map<string, EIP1193Provider>();
  const listen = (event: Event) => {
    const detail: unknown = Reflect.get(event, "detail");
    if (typeof detail !== "object" || detail === null) return;
    const info: unknown = Reflect.get(detail, "info");
    const rdns: unknown =
      typeof info === "object" && info !== null
        ? Reflect.get(info, "rdns")
        : undefined;
    const provider: unknown = Reflect.get(detail, "provider");
    if (
      typeof rdns === "string" &&
      METAMASK_RDNS.includes(rdns) &&
      !found.has(rdns) &&
      isEip1193(provider) &&
      isGenuineMetaMask(provider)
    )
      found.set(rdns, provider);
  };
  window.addEventListener("eip6963:announceProvider", listen);
  try {
    window.dispatchEvent(new Event("eip6963:requestProvider"));
  } finally {
    window.removeEventListener("eip6963:announceProvider", listen);
  }
  for (const rdns of METAMASK_RDNS) {
    const provider = found.get(rdns);
    if (provider) return provider;
  }
  return undefined;
}

/** The extension's EVM provider, or undefined where it is not installed. */
export function injectedMetaMask(): EIP1193Provider | undefined {
  if (typeof window === "undefined") return undefined;
  const announced = announcedMetaMask();
  if (announced) return announced;
  const flagged = injectedProviders(Reflect.get(window, "ethereum")).find(
    isGenuineMetaMask
  );
  return isEip1193(flagged) ? flagged : undefined;
}

/**
 * RainbowKit's MetaMask entry on a gated connector that never revokes.
 *
 * The connector id stays `metaMask`, which is what wagmi's own storage keys
 * (and the seed) are written under. The provider is resolved once found and
 * then kept, so the EIP-6963 request is not repeated on every call.
 */
export function gatedMetaMaskEntry(wallet: Wallet): Wallet {
  return {
    ...wallet,
    installed: true,
    createConnector: (walletDetails: WalletDetailsParams) =>
      createConnector((config) => {
        let resolved: EIP1193Provider | undefined;
        const base = injected({
          target: () => ({
            id: "metaMask",
            name: "MetaMask",
            provider: () => (resolved ??= injectedMetaMask()),
          }),
        })(config);
        return {
          ...chosenOnly(METAMASK_EVM_CHOSEN, base),
          ...walletDetails,
          async getProvider() {
            const provider = await base.getProvider.call(this);
            return provider ? keepPermissions(provider) : provider;
          },
        };
      }),
  };
}

/**
 * Carries an existing MetaMask EVM connection across the gate, once
 * (`seedChosen`). A holder who reached MetaMask through "Browser Wallet" is
 * not carried: that entry is withdrawn in front of MetaMask, so they pick
 * MetaMask once by name. Accepted: this runs only where MetaMask is offered
 * for Solana, which is not production.
 */
export function seedMetaMaskChoice(): void {
  seedChosen({
    key: METAMASK_EVM_CHOSEN,
    connectorId: "metaMask",
    seeded: "mudwater.metamask-evm.migrated",
  });
}

/**
 * RainbowKit's Rabby entry, made safe where Rabby is absent (final review,
 * S4).
 *
 * Absent, RainbowKit's connector for it is wagmi's untargeted `injected()`,
 * with the id "injected" and no gate: once storage holds `injected.connected`
 * (any untargeted injected connect writes it), every load's `reconnect` let
 * it adopt whatever held `window.ethereum`, around D6's gate and PR 1's
 * withdrawal. RainbowKit offers an uninstalled extension as an install link,
 * never a connect, so gating it costs nothing reachable. Installed, the entry
 * is RainbowKit's own, targeted at the `isRabby` provider, as it always was.
 */
export function rabbyEntry(wallet: Wallet): Wallet {
  const installed =
    typeof window !== "undefined" &&
    injectedProviders(Reflect.get(window, "ethereum")).some(
      (provider) =>
        typeof provider === "object" &&
        provider !== null &&
        Reflect.get(provider, "isRabby") === true
    );
  // Installed, or a flag-unset build (production): RainbowKit's own, as main.
  if (installed || !isGenericEnabled()) return wallet;
  return {
    ...wallet,
    createConnector: (walletDetails: WalletDetailsParams) =>
      createConnector((config) => ({
        ...chosenOnly("mudwater.rabby-absent.chosen", injected()(config)),
        ...walletDetails,
      })),
  };
}
